import { useState, useCallback, useMemo, useRef } from 'react';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';

import { useSalesOrder } from '../../hooks';
import { useSession } from '~/modules/sessions/hooks/useSession';

import validatorSchema from '../../validators/salesOrder';
import CustomRowHeadCells from '~/shared/components/TableList/CustomRowHeadCells';
import CustomRowOtherCells from '~/shared/components/TableList/CustomRowOtherCells';
import ReprocessDialog from '~/shared/components/Dialogs/ReprocessDialog';
import WarningDialog from '~/shared/components/Dialogs/WarningDialog';
import TableList, {
  WrapperContainer,
} from '~/shared/components/TableList/TableList';

import { formDataParser } from '../../sanitizers/salesOrder';

import { tableListOptions } from '../../constantVariables/salesOrder';

function SalesOrdersList() {
  const tableListRef = useRef();

  const { current_accesses } = useSession();

  const {
    listLoading,
    index,
    reprocessAll,
    reprocessUnitary,
    generateJSON,
    destroy,
  } = useSalesOrder();

  const [showDialog, setShowDialog] = useState(false);
  const [showWarningDialog, setShowWarnigDialog] = useState(false);

  const loadSalesOrders = useCallback(
    (search = '', order_by = 'id', order = 'asc', page = 1, filter_by = '') => {
      return index({
        search,
        order_by,
        order,
        page,
        filter_by,
      });
    },
    [index]
  );

  const handleReprocess = async ({ type, indicator, start_at }) => {
    await reprocessAll({ type, indicator, start_at });
    setShowDialog(false);
  };

  const newItem = useMemo(() => {
    if (
      current_accesses.roles['farm.admin'] ||
      current_accesses.roles['farm.manager'] ||
      current_accesses.permissions['farm.sales-orders.patch']
    ) {
      return {
        label: 'Reprocessar Todos',
        handleFunction: () => {
          setShowDialog(true);
        },
      };
    }

    return null;
  }, [current_accesses]);

  const handleRemove = useCallback(
    async ({ dataObj }) => {
      const removedData = await destroy({
        dataObj: { ...dataObj, req_id: '1' },
      });

      if (removedData.uuid) {
        tableListRef.current.handleRemoveData({
          key: 'uuid',
          value: removedData,
        });
      }

      setShowWarnigDialog(false);
    },
    [destroy]
  );

  const handleReprocessUnitary = async ({ indicator, row, origin }) => {
    await reprocessUnitary({ indicator, row, origin });
    await loadSalesOrders();
  };

  const handleGenerateJSON = async ({ indicator, row, origin }) => {
    await generateJSON({ indicator, row, origin });
  };

  const reprocessModal = useMemo(() => {
    const auxReprocessModal = [
      {
        value: 'ecommerce',
        label: 'E-Commerce',
      },
      {
        value: 'erp',
        label: 'ERP',
      },
    ];
    return auxReprocessModal;
  }, []);

  const reprocessFields = useMemo(() => {
    const auxReprocessFields = [
      {
        value: 'sales_order',
        label: 'Pedidos de Vendas',
      },
      {
        value: 'invoice',
        label: 'NFE',
      },
    ];
    return auxReprocessFields;
  }, []);

  const actions = useMemo(() => {
    const auxActions = [];

    if (
      current_accesses.roles['farm.admin'] ||
      current_accesses.roles['farm.manager'] ||
      current_accesses.permissions['farm.sales-orders.delete'] ||
      current_accesses.permissions['farm.sales-orders/:uuid.patch']
    ) {
      auxActions.push({
        label: 'Remover',
        icon: <DeleteForeverIcon fontSize="small" />,
        handleFunction: async salesOrder => {
          handleRemove({ dataObj: salesOrder });
        },
      });
    }

    return auxActions;
  }, [handleRemove, current_accesses]);

  return (
    <WrapperContainer>
      <ReprocessDialog
        showDialog={showDialog}
        setShowDialog={setShowDialog}
        onSubmit={handleReprocess}
        dataParser={formDataParser}
        validatorSchema={validatorSchema}
        loading={listLoading}
        typesOptions={tableListOptions.typesOptions}
        indicatorOptions={tableListOptions.reprocessOptions}
        dataInput
      />
      <WarningDialog
        showDialog={showWarningDialog}
        setShowDialog={setShowWarnigDialog}
        description="Tem certeza que deseja remover o Pedido de Venda?"
        onSuccess={handleRemove}
        loading={listLoading}
      />
      <TableList
        title=""
        newItem={newItem}
        headCells={tableListOptions.headCells}
        actions={actions}
        observationCells={tableListOptions.observationCells}
        loadData={loadSalesOrders}
        customRowHeadCells={CustomRowHeadCells}
        customRowOtherCells={CustomRowOtherCells}
        loading={listLoading}
        otherCells={tableListOptions.otherCells}
        reprocessModal={reprocessModal}
        reprocessFields={reprocessFields}
        reprocessSubmit={handleReprocessUnitary}
        generateJSON={handleGenerateJSON}
        ref={tableListRef}
        filtersOptions={tableListOptions.filtersOptions}
        idFilterInitial="ecommerce_id"
      />
    </WrapperContainer>
  );
}

export default SalesOrdersList;
